import React, { useCallback, useEffect, useState } from 'react';
import {
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    CardContent,
    CardHeader,
    Chip,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Accordion, FormCard, Loader, Menu } from '../../../../../../Components';
import { setModal } from '../../../../../Common/App';
import {
    DeleteEventModal,
    EventModal,
    fetchEvents,
} from '../../../index';
import { usePermissions } from '../../../../../../Hooks/usePermissions';

const summarySx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row-reverse',
};

export const EventTab = () => {

    const dispatch = useDispatch();
    const eventsDirty = useSelector((store) => store.settings?.events?.is_dirty);
    const [isLoading, setIsLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [menuState, setMenuState] = useState({});
    const { hasPermission, hasAnyPermission } = usePermissions();

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        await fetchEvents({
            dispatch,
            filters: {
                paginate: false,
                sort_by: 'event_name',
                sort_dir: 'asc',
                columns: [
                    'id',
                    'event_name',
                    'description',
                    'venue_id',
                    'location_id',
                    'start_date',
                    'end_date',
                    'is_active',
                ],
            },
        }).then((res) => {
            if (res.success) {
                const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
                setItems(data);
            }
        });
        setIsLoading(false);
    }, [dispatch]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (eventsDirty) {
            fetchData();
        }
    }, [eventsDirty, fetchData]);

    const handleClick = (event, id) => {
        event.stopPropagation();
        setMenuState((prev) => ({
            ...prev,
            [id]: event.currentTarget,
        }));
    };

    const handleClose = (id) => {
        setMenuState((prev) => ({
            ...prev,
            [id]: null,
        }));
    };

    const handleDelete = (e, row) => {
        e.stopPropagation();
        dispatch(setModal({ component: <DeleteEventModal event={row} />, open: true }));
    };

    const handleEdit = (e, row) => {
        e.stopPropagation();
        dispatch(setModal({ component: <EventModal item={row} />, open: true }));
    };

    if (!hasPermission('core.settings.event.menu')) {
        return (
            <FormCard>
                <CardHeader title="Event management" />
                <CardContent>
                    <Typography variant="body2" color="text.secondary">
                        You do not have access to event management. Ask an administrator to grant the{' '}
                        <strong>core.settings.event.menu</strong> permission.
                    </Typography>
                </CardContent>
            </FormCard>
        );
    }

    return (
        <FormCard>
            <CardHeader
                title="Event management"
                avatar={<Loader isLoading={isLoading} type="linear" />}
                action={hasPermission('core.settings.event.add') ? (
                    <Button
                        variant="contained"
                        size="small"
                        color="info"
                        onClick={() => dispatch(setModal({ component: <EventModal />, open: true }))}
                    >
                        + New Event
                    </Button>
                ) : null}
            />
            <CardContent sx={{ pb: '1rem!important' }}>
                <Grid container spacing={2}>
                    {items.length > 0 ? (
                        <Grid item xs={12}>
                            {items.map((row) => {
                                const menuKey = `event-${row.id}`;
                                const statusLabel = row.is_active ? 'Active' : 'Inactive';

                                return (
                                    <Accordion key={row.id} rounded={0} elevation={0}>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls={`panel-event-${row.id}-content`}
                                            id={`panel-event-${row.id}-header`}
                                            sx={summarySx}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                                                <Typography sx={{ flexGrow: 1 }}>{row.event_name}</Typography>
                                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                                    <Chip
                                                        variant="outlined"
                                                        color={row.is_active ? 'primary' : 'error'}
                                                        size="small"
                                                        label={statusLabel}
                                                    />
                                                </Stack>
                                            </Box>
                                            {hasAnyPermission(['core.settings.event.edit', 'core.settings.event.delete']) && (
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <IconButton
                                                        aria-label="more"
                                                        id={`menu-button-${menuKey}`}
                                                        aria-controls={menuState[menuKey] ? `menu-${menuKey}` : undefined}
                                                        aria-expanded={menuState[menuKey] ? 'true' : undefined}
                                                        aria-haspopup="true"
                                                        onClick={(e) => handleClick(e, menuKey)}
                                                    >
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                    <Menu
                                                        id={`menu-${menuKey}`}
                                                        anchorEl={menuState[menuKey]}
                                                        open={Boolean(menuState[menuKey])}
                                                        onClose={() => handleClose(menuKey)}
                                                        anchorOrigin={{
                                                            vertical: 'bottom',
                                                            horizontal: 'right',
                                                        }}
                                                        transformOrigin={{
                                                            vertical: 'top',
                                                            horizontal: 'right',
                                                        }}
                                                    >
                                                        {hasPermission('core.settings.event.edit') && (
                                                            <MenuItem onClick={(e) => handleEdit(e, row)}>
                                                                <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
                                                            </MenuItem>
                                                        )}
                                                        {hasPermission('core.settings.event.delete') && (
                                                            <MenuItem onClick={(e) => handleDelete(e, row)}>
                                                                <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
                                                            </MenuItem>
                                                        )}
                                                    </Menu>
                                                </Box>
                                            )}
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <List>
                                                <ListItem>
                                                    <ListItemText
                                                        primary={row.is_active ? 'Active' : 'Inactive'}
                                                        primaryTypographyProps={{
                                                            color: row.is_active ? 'primary.main' : 'error.main',
                                                            fontWeight: 500,
                                                        }}
                                                    />
                                                </ListItem>
                                            </List>
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            })}
                        </Grid>
                    ) : (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                                No events yet. Add your first event to get started.
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </CardContent>
        </FormCard>
    );
};
